import Barchart from 'components/molecules/Barchart';
import { classToRemoveScrollbar } from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import StackedBarChart from 'components/molecules/StackedBarChart';
import { promoCalenderId, TileData } from 'components/template/BumpReport/MainSection';
import { colorConstants } from 'config/colorConstants';
import { COMPARISON_CHART_CONTAINER_IDS } from 'config/constants';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useRef } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
} from 'utils/helperMethods';
import {
    calculateChartWidth,
    chartHeight,
    defaultBarLength,
    sliderPaddingMapping,
    yAxisWidth,
} from './BumpChartContainer';
import { BumpReportFromType } from 'types/common';

interface Props {
    sliderValue: number;
    chartConfig: any;
    setChartConfig: Function;
    tileData: TileData;
    chartData: any;
    optionSelected: any;
    timeBucketValue: number | null;
    showAllLabels: boolean;
    enableLineChart: boolean;
    chartContainers: [string, string, string];
    from: BumpReportFromType;
}

const BumpChartComparisonContainer: FC<Props> = ({
    optionSelected,
    chartData,
    sliderValue,
    chartConfig,
    setChartConfig,
    tileData,
    timeBucketValue,
    showAllLabels,
    enableLineChart,
    chartContainers,
    from = 'bump-report',
}) => {
    const chartRef = useRef<any>(null);
    const { appConfig } = useContext(UserContext);
    const isShipmentBumpReport = from === 'shipment-bump-report';
    const bumpReportConfig = getConfig(isShipmentBumpReport ? 'shipmentBumpReport' : 'bumpReport', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const tooltipKeyLabelMapping = bumpReportConfig.tooltipKeyLabelMapping;
    const tooltipExtraItems = bumpReportConfig.tooltipExtraItems;
    const isTableScrolling = useRef(false);
    const tooltipItemCache = useRef<any>({});
    const groupByKey = bumpReportConfig.groupByKey;
    const { data, loading, error, color } = chartData;
    const totalBarsLength = data ? Object.keys(data[tileData.absTile.key]?.extras || {}).length : 0;
    const tileChartData = data ? data[tileData.absTile.key] : null;
    useEffect(() => {
        const { updatedBarWidth, updatedWidth } = calculateChartWidth(
            chartRef.current?.offsetWidth || chartConfig.width,
            sliderValue,
            totalBarsLength || defaultBarLength,
        );

        setChartConfig({ barWidth: updatedBarWidth, width: updatedWidth });
    }, [sliderValue, totalBarsLength]);

    useEffect(() => {
        // promoCalenderScrollElement?.scrollTo({
        //     left: chartConfig.width,
        //     behavior: 'smooth',
        // });
        chartRef.current?.scroll(chartConfig.width, 0);
    }, [chartConfig, tileData.bumpReportTile]);

    // listening to scroll - sync between two chart components
    useEffect(() => {
        // Table element
        const graphItem1 = document.getElementById(COMPARISON_CHART_CONTAINER_IDS[0]);
        // Graph element
        const graphItem2 = document.getElementById(COMPARISON_CHART_CONTAINER_IDS[1]);

        // Function to synchronize scrolling of graph and table
        function scrollFunction(event: any) {
            if (event.target === graphItem1) {
                // If graph is scrolled, scroll the table
                graphItem2?.scrollTo?.({
                    left: Number(graphItem1?.scrollLeft) * 1, // Giving a buffer because widths of table and chart are different
                    behavior: 'auto',
                });
            } else {
                // If table is scrolled, scroll the graph
                graphItem1?.scrollTo?.({
                    left: Number(graphItem2?.scrollLeft) * 1,
                    behavior: 'auto',
                });
            }
        }

        // Add event listeners for both graph and table
        graphItem1?.addEventListener?.('scroll', scrollFunction, { passive: true });
        graphItem2?.addEventListener?.('scroll', scrollFunction, { passive: true });

        return () => {
            // Cleanup: remove event listeners
            graphItem1?.removeEventListener?.('scroll', scrollFunction);
            graphItem2?.removeEventListener?.('scroll', scrollFunction);
        };
    }, [chartData, chartContainers]);
    // listening to table scroll and scrolling graph
    // useEffect(() => {
    //     const promoCalenderScrollElement = document.getElementById(COMPARISON_CHART_CONTAINER_IDS[1]);

    //     //scroll table when user is scrolling bump chart
    //     function scrollFunction(event: any) {
    //         setTimeout(() => {
    //             chartRef.current?.scrollTo({
    //                 left: Number(promoCalenderScrollElement?.scrollLeft) * 1, //giving a speed because widths of table and chart are different
    //                 behavior: 'auto',
    //             });
    //         }, 5);
    //         // adding some gap here to avoid multiple function call
    //         setTimeout(() => {
    //             promoCalenderScrollElement?.scrollTo({
    //                 left: Number(chartRef.current?.scrollLeft) * 1, //giving a speed because widths of table and chart are different
    //                 behavior: 'auto',
    //             });
    //         }, 5);
    //     }
    //     //doing this to avoid scroll event being called when scrolling graph
    //     function mouseEnterEventFunction() {
    //         isTableScrolling.current = true;
    //         promoCalenderScrollElement?.addEventListener('scroll', scrollFunction, { passive: true });
    //     }
    //     function mouseLeaveEventFunction() {
    //         isTableScrolling.current = false;
    //         promoCalenderScrollElement?.removeEventListener('scroll', scrollFunction);
    //     }
    //     promoCalenderScrollElement?.addEventListener('mouseenter', mouseEnterEventFunction);
    //     promoCalenderScrollElement?.addEventListener('mouseleave', mouseLeaveEventFunction);
    //     return () => {
    //         promoCalenderScrollElement?.removeEventListener('mouseenter', mouseEnterEventFunction);
    //         promoCalenderScrollElement?.removeEventListener('mouseleave', mouseLeaveEventFunction);
    //     };
    // }, []);

    const currency =
        tileData?.volTile?.key === 'val' && tileData?.absTile.key === 'abs' ? fetchCurrency(appConfig) : '';
    return (
        <section>
            <div className="flex">
                {/* Y1 AXIS */}
                <div style={{ width: yAxisWidth + 30 }}>
                    <Barchart
                        // padding={[0, 0, 0, 48]}
                        padding={[0, 0, 17, 0]}
                        barWidth={chartConfig.barWidth}
                        xAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`${chartContainers[0]}`}
                        fields={['date', 'sales']}
                        //  minBound={scaleMinValue(40, tileChartData?.minVal)}
                        minBound={tileChartData?.minVal}
                        maxBound={
                            tileData?.absTile?.key === 'percentage'
                                ? tileChartData?.maxVal
                                : scaleMaxValue(40, tileChartData?.maxVal)
                        }
                        width={yAxisWidth + 30}
                        colorKey={'type'}
                        yAxisLabelFormatter={(val: any) => {
                            return tileData?.absTile?.key === 'percentage'
                                ? formatNumWithKMBAlt(val, decimalForPercentage)
                                : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        // Yline
                        YGrid={false}
                    />
                </div>
                {/* CHART COMPONENT */}
                <div
                    id={`${chartContainers[1]}`}
                    ref={chartRef}
                    // style={{ width: chartConfig.width || 'auto' }}
                    className={`overflow-x-auto w-full pb-[60px] ${classToRemoveScrollbar}`}
                >
                    <StackedBarChart
                        totalKey={tooltipKeyLabelMapping[tileData.bumpReportTile.key]}
                        barWidth={chartConfig.barWidth}
                        // yAxisConfig={false}
                        lineChartParams={
                            !enableLineChart
                                ? null
                                : {
                                      lineColors: tileChartData?.lineColors?.map((item: any) => item?.color) || [],
                                      fields: tileChartData?.lineFields || [],
                                      minBound: tileChartData?.lineMinVal,
                                      maxBound: tileChartData?.lineMaxVal,
                                      color: colorConstants.darkBlueAlt,
                                      disableIntervalLabel: true,
                                      tooltipFormatter: (xAxis: any, val: any, from: any) => {
                                          let option: any = {};
                                          //checking if item exists in cache
                                          if (from in tooltipItemCache.current) option = tooltipItemCache.current[from];
                                          else {
                                              option = optionSelected?.filter((item: any) => {
                                                  return item?.value === from;
                                              })?.[0];
                                              if (option) tooltipItemCache.current[from] = option;
                                          }

                                          return {
                                              name: option ? option?.label : from,
                                              value:
                                                  (tileData.absTile.key === 'percentage' ? '' : option?.unit || '') +
                                                  formatNumWithKMBAlt(
                                                      val,
                                                      decimalForNonPercentage,
                                                      decimalForNonPercentage,
                                                  ),
                                          };
                                      },
                                  }
                        }
                        intervalFormatter={(val) => {
                            return tileData.absTile.key === 'percentage'
                                ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                : currency + formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        tooltipFormatter={(xAxis: any, val: any) => {
                            return {
                                name: xAxis,
                                value:
                                    tileData.absTile.key === 'percentage'
                                        ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                                        : currency +
                                          formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                            };
                        }}
                        dataset={tileChartData?.data || []}
                        extras={{ data: tileChartData?.extras, dataMapping: tooltipExtraItems }}
                        height={chartHeight}
                        //  showLegend
                        loading={false}
                        chartContainer={`${chartContainers[1]}`}
                        fields={['date', 'sales', 'type', 'value', 'index', 'areaData']}
                        areaChartParams={
                            tileData?.absTile?.key === 'percentage' || tileData.bumpReportTile.key === groupByKey
                                ? undefined
                                : undefined

                            // {
                            //       tooltipLabel: 'YA Sales',
                            //       minBound: tileChartData?.minVal,
                            //       maxBound:
                            //           tileData?.absTile?.key === 'percentage'
                            //               ? tileChartData?.maxVal
                            //               : scaleMaxValue(40, tileChartData?.maxVal),
                            //   }
                        }
                        //  minBound={scaleMinValue(40, tileChartData?.minVal)}
                        minBound={tileChartData?.minVal}
                        maxBound={
                            tileData?.absTile?.key === 'percentage'
                                ? tileChartData?.maxVal
                                : scaleMaxValue(40, tileChartData?.maxVal)
                        }
                        width={Math.floor(chartConfig.width) || window.innerWidth}
                        xAxisLabelFormatter={(val: any, item: any, index: number) => {
                            if (index % 4 === 0) return val;
                        }}
                        Xline={false}
                        skipIntervalLabel={showAllLabels ? 1 : 4}
                        colorKey={'type'}
                        intervalLabelKey="totalSales"
                        groupedChartColor={color}
                        currency={currency}
                    />
                </div>

                {/* Y2 AXIS */}
                <div className={classToRemoveScrollbar} style={{ width: yAxisWidth+10 }}>
                    {tileData.bumpReportTile.key !== groupByKey ? (
                        <Barchart
                            padding={[0, 0, 19, 18]}
                            barWidth={chartConfig.barWidth}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`${chartContainers[2]}`}
                            fields={['date', 'value']}
                            minBound={tileChartData?.lineMinVal || 0}
                            maxBound={tileChartData?.lineMaxVal}
                            width={yAxisWidth+10}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            YGrid={false}
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default React.memo(BumpChartComparisonContainer);
